import { createGlobalStyle } from 'styled-components';

import type { DsTheme } from './theme';

/**
 * A barra de rolagem da interface, vertical e horizontal, num lugar só.
 *
 * Antes deste arquivo cada tela resolvia por conta própria. Dois modais da Loja
 * desenhavam o polegar em preto puro sobre um trilho de sombra preta — preto
 * puro que o guia não admite nem em sombra, e que no tema escuro simplesmente
 * desaparecia. Todo o resto (gaveta, lista de filtro, painéis do Designer,
 * tabela densa, modais do design system) ficava com a barra do sistema
 * operacional: cinza-claro larga, sobre o roxo do tema escuro. E o Firefox não
 * era atendido em lugar nenhum, porque ninguém declarava `scrollbar-color`.
 *
 * O padrão daqui: canal fino, trilho translúcido que deixa o fundo de quem rola
 * aparecer, polegar no neutro do tema e, no hover, o acento — o mesmo roxo do
 * anel de foco e do item ativo, para a marca dominar também aqui.
 */

/** Canal de 10px; a folga transparente estreita trilho e polegar para 6px. */
const CANAL = '10px';
const FOLGA = '2px';

/** Os tokens do tema chegam pelo provedor, como no estilo legado ao lado. */
interface PropsDoTema {
  theme: DsTheme;
}

export const EstilosDeRolagem = createGlobalStyle<PropsDoTema>`
  /*
   * O Firefox não tem pseudo-elemento de barra: só aceita a espessura nominal e
   * o par polegar/trilho, herdados a partir do html. Perde o raio de cápsula e
   * o acento no hover, que só existem nos pseudo-elementos, mas ganha a cor do
   * tema — antes ficava com a barra do sistema.
   *
   * A trava do @supports não é cosmética. A partir do Chrome 121 as duas
   * propriedades passaram a ser implementadas e vencem os pseudo-elementos, e
   * scrollbar-color é herdada: declarada no html sem trava, ela desligava o
   * bloco webkit inteiro árvore abaixo — a barra voltava a ser a do sistema,
   * com 15px, e nada daqui tinha efeito. A consulta por selector() separa os
   * dois mundos: quem conhece o pseudo-elemento não entra neste bloco.
   */
  @supports not selector(::-webkit-scrollbar) {
    html {
      scrollbar-width: thin;
      scrollbar-color: ${({ theme }) =>
        `${theme.colors.scrollPolegar} ${theme.colors.scrollTrilha}`};
    }
  }

  /*
   * O seletor universal é intencional. O Chrome só pinta a barra de quem casa
   * com o seletor, e a interface tem dezenas de contêineres que rolam; listá-los
   * um a um seria garantir que o próximo nasça com a barra do sistema de novo.
   */
  *::-webkit-scrollbar {
    width: ${CANAL};
    height: ${CANAL};
  }

  /*
   * Trilho e polegar recuam a folga do canal pela borda transparente com
   * recorte no padding — sobram 6px visíveis, na grade de 4px, sem precisar de
   * dois tamanhos diferentes nem de um trilho maior que o polegar.
   */
  *::-webkit-scrollbar-track {
    background-color: ${({ theme }) => theme.colors.scrollTrilha};
    background-clip: padding-box;
    border: ${FOLGA} solid transparent;
    border-radius: ${({ theme }) => theme.radius.pill};
  }

  *::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.scrollPolegar};
    background-clip: padding-box;
    border: ${FOLGA} solid transparent;
    border-radius: ${({ theme }) => theme.radius.pill};
  }

  /*
   * O único estado da barra que muda de cor. Serve de resposta ao ponteiro num
   * controle que não tem rótulo, e continua valendo enquanto se arrasta.
   */
  *::-webkit-scrollbar-thumb:hover,
  *::-webkit-scrollbar-thumb:active {
    background-color: ${({ theme }) => theme.colors.accent};
  }

  /* Onde a vertical encontra a horizontal; sem isto o Chrome pinta branco. */
  *::-webkit-scrollbar-corner {
    background-color: ${({ theme }) => theme.colors.scrollTrilha};
  }

  /*
   * As setas das pontas: o Chrome no Windows passa a desenhá-las quando a barra
   * é estilizada, e elas nascem sem cor — dois quadrados vazios em cada ponta.
   */
  *::-webkit-scrollbar-button {
    display: none;
  }
`;
